import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  api,
  type DuplicateCandidate,
  type GuestSummary,
} from '../lib/api.js';
import { useSession, useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { formatDate } from '../lib/time.js';
import { StatusPill } from '../components/StatusPill.js';

interface GuestProfile {
  guest: GuestSummary & {
    tags: string[] | null;
    dietary: string[] | null;
    internal_notes: string | null;
    seating_preferences: string | null;
    version: number;
  };
  stats: {
    total_visits: number;
    total_covers: number;
    no_show_count: number;
    late_cancel_count: number;
    last_visit_at: string | null;
  };
  visits: Array<{
    id: string;
    booking_ref: string;
    service_date: string;
    party_size: number;
    status: string;
    source: string;
    occasion: string | null;
  }>;
  messages: Array<{
    id: string;
    direction: string;
    template_name: string | null;
    body_rendered: string;
    status: string;
    created_at: string;
  }>;
}

/**
 * The guest CRM.
 *
 * The merge panel is the part that needed care. Merging destroys one record, so
 * it shows a field-by-field diff and the SCORE with its reasons, and the host
 * chooses which side wins for each conflict. Nothing is pre-selected unless the
 * matcher was confident, and a shared-number match never is (D-026).
 */
export function GuestsScreen() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const { can } = useSession();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState<DuplicateCandidate | null>(null);
  const [choices, setChoices] = useState<Record<string, 'surviving' | 'merged'>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['guests', venueId, search],
    enabled: Boolean(venueId),
    queryFn: () =>
      api.get<{ guests: GuestSummary[] }>('/api/v1/guests', {
        venue_id: venueId,
        ...(search.trim() ? { q: search.trim() } : {}),
      }),
  });

  const profile = useQuery({
    queryKey: ['guest', selectedId, venueId],
    enabled: Boolean(selectedId),
    queryFn: () => api.get<GuestProfile>(`/api/v1/guests/${selectedId}`, { venue_id: venueId }),
  });

  const duplicates = useQuery({
    queryKey: ['guest-duplicates', selectedId, venueId],
    enabled: Boolean(selectedId) && can('guest:merge'),
    queryFn: () =>
      api.get<{ candidates: DuplicateCandidate[] }>(
        `/api/v1/guests/${selectedId}/duplicates`,
        { venue_id: venueId },
      ),
  });

  const merge = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/guests/${selectedId}/merge`, {
        venue_id: venueId,
        merge_guest_id: mergeTarget!.guest_id,
        field_choices: choices,
      }),
    onSuccess: () => {
      setMergeTarget(null);
      setChoices({});
      setNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['guests'] });
      void queryClient.invalidateQueries({ queryKey: ['guest'] });
      void queryClient.invalidateQueries({ queryKey: ['guest-duplicates'] });
    },
    onError: (err: unknown) =>
      setNotice(err instanceof ApiError ? err.message : t('errors.generic')),
  });

  const otherProfile = useQuery({
    queryKey: ['guest', mergeTarget?.guest_id, venueId],
    enabled: Boolean(mergeTarget),
    queryFn: () =>
      api.get<GuestProfile>(`/api/v1/guests/${mergeTarget!.guest_id}`, { venue_id: venueId }),
  });

  const DIFF_FIELDS = ['first_name', 'last_name', 'name_ar', 'email', 'phone_e164', 'allergies'] as const;

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <section className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{t('guests.title')}</h1>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('guests.search')}
          className="rounded-md border border-slate-200 px-3 py-2 text-sm"
        />

        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {(list.data?.guests ?? []).map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => {
                  setSelectedId(row.id);
                  setMergeTarget(null);
                }}
                className={[
                  'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-start hover:bg-brand-teal-light',
                  selectedId === row.id ? 'bg-brand-teal-light' : '',
                ].join(' ')}
              >
                <span className="text-sm font-medium">
                  {[row.first_name, row.last_name].filter(Boolean).join(' ') ||
                    row.name_ar ||
                    t('common.guest')}
                </span>
                <span className="text-xs text-slate-500">
                  <span data-numeric>{row.total_visits}</span> {t('guests.visits')}
                  {row.vip_level > 0 ? ` · ${t('guests.vip')} ${row.vip_level}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {list.data?.guests.length === 0 ? (
          <p className="p-4 text-center text-sm text-slate-500">{t('guests.empty')}</p>
        ) : null}
      </section>

      <section className="flex flex-col gap-4">
        {notice ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{notice}</p>
        ) : null}

        {!selectedId ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
            {t('guests.profile')}
          </p>
        ) : null}

        {profile.data ? (
          <>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h2 className="text-xl font-semibold">
                {[profile.data.guest.first_name, profile.data.guest.last_name]
                  .filter(Boolean)
                  .join(' ') || profile.data.guest.name_ar}
              </h2>

              {profile.data.guest.allergies ? (
                <p className="mt-2 rounded bg-red-50 px-2 py-1 text-sm text-red-700">
                  {t('guests.notes')}: {profile.data.guest.allergies}
                </p>
              ) : null}

              <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ['guests.visits', profile.data.stats.total_visits],
                  ['guests.covers', profile.data.stats.total_covers],
                  ['guests.noShows', profile.data.stats.no_show_count],
                  ['guests.lateCancels', profile.data.stats.late_cancel_count],
                ].map(([key, value]) => (
                  <div key={String(key)}>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">
                      {t(String(key))}
                    </dt>
                    <dd className="font-mono text-lg" data-numeric>
                      {String(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-semibold">{t('guests.visits')}</h3>
              <ul className="divide-y divide-slate-100">
                {profile.data.visits.map((visit) => (
                  <li key={visit.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="font-mono text-xs text-slate-500">
                      {formatDate(visit.service_date, venue, i18n.language)}
                    </span>
                    <span data-numeric>{visit.party_size}</span>
                    <StatusPill status={visit.status} />
                  </li>
                ))}
              </ul>
            </div>

            {can('guest:merge') && duplicates.data?.candidates.length ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                <h3 className="text-sm font-semibold text-amber-900">
                  {t('guests.duplicates')}
                </h3>
                <ul className="mt-2 space-y-2">
                  {duplicates.data.candidates.map((candidate) => (
                    <li
                      key={candidate.guest_id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded bg-white p-2"
                    >
                      <div>
                        <p className="text-sm font-medium">
                          {[candidate.first_name, candidate.last_name]
                            .filter(Boolean)
                            .join(' ') || candidate.name_ar}
                        </p>
                        {/* The reasons, never a bare score. A host about to
                            delete a record needs to see the machine's argument. */}
                        <ul className="text-xs text-slate-600">
                          {candidate.reasons.map((reason) => (
                            <li key={reason}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setMergeTarget(candidate);
                          setChoices({});
                        }}
                        className="rounded-md border border-amber-400 px-3 py-1 text-sm hover:bg-amber-100"
                      >
                        {t('guests.merge')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      {mergeTarget && profile.data && otherProfile.data ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex max-h-full w-full max-w-2xl flex-col gap-3 overflow-y-auto rounded-lg bg-white p-5">
            <h2 className="text-lg font-semibold">{t('guests.confirmMerge')}</h2>
            <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('guests.mergeWarning')}
            </p>

            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="py-1 text-start">{t('guests.field')}</th>
                  <th scope="col" className="py-1 text-start">{t('guests.surviving')}</th>
                  <th scope="col" className="py-1 text-start">{t('guests.mergedAway')}</th>
                </tr>
              </thead>
              <tbody>
                {DIFF_FIELDS.map((field) => {
                  const mine = (profile.data.guest as unknown as Record<string, unknown>)[field];
                  const theirs = (otherProfile.data.guest as unknown as Record<string, unknown>)[field];
                  const differs = String(mine ?? '') !== String(theirs ?? '');

                  return (
                    <tr key={field} className={differs ? 'bg-amber-50' : ''}>
                      <td className="py-1 font-mono text-xs">{field}</td>
                      <td className="py-1">
                        <label className="flex items-center gap-1.5">
                          {differs ? (
                            <input
                              type="radio"
                              name={field}
                              checked={(choices[field] ?? 'surviving') === 'surviving'}
                              onChange={() =>
                                setChoices((prev) => ({ ...prev, [field]: 'surviving' }))
                              }
                            />
                          ) : null}
                          <span>{String(mine ?? '')}</span>
                        </label>
                      </td>
                      <td className="py-1">
                        <label className="flex items-center gap-1.5">
                          {differs ? (
                            <input
                              type="radio"
                              name={field}
                              checked={choices[field] === 'merged'}
                              onChange={() =>
                                setChoices((prev) => ({ ...prev, [field]: 'merged' }))
                              }
                            />
                          ) : null}
                          <span>{String(theirs ?? '')}</span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={merge.isPending}
                onClick={() => merge.mutate()}
                className="rounded-md bg-brand-teal px-4 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
              >
                {t('guests.confirmMerge')}
              </button>
              <button
                type="button"
                onClick={() => setMergeTarget(null)}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
